/**
 * Minimal iCalendar (.ics) generator for booked audition calls (LG-118).
 *
 * Google emails its own invite to the applicant as an attendee; this is the
 * fallback for people who don't use Google Calendar, and it is offered from
 * the confirmation screen the moment they pick a slot — i.e. possibly before
 * the Google event (and its Meet link) exists.
 *
 * Pure and client-safe: everything time-dependent (`stamp`, `uid`) is passed
 * in, so the output is deterministic and the browser can build the blob.
 */

export interface IcsArgs {
  start: Date
  durationMinutes: number
  summary: string
  description?: string
  meetingUrl?: string | null
  /** Globally unique id for the event — use the CallProposal id. */
  uid: string
  /** DTSTAMP value; caller supplies it so this stays pure. */
  stamp: Date
}

/** iCalendar UTC form: YYYYMMDDTHHMMSSZ. */
function toIcsUtc(date: Date): string {
  return `${date.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`
}

/**
 * RFC 5545 §3.3.11 TEXT escaping. Commas and semicolons are value
 * separators, so an unescaped one in a summary silently truncates the field.
 */
function escapeText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r\n|\n|\r/g, '\\n')
}

export function buildIcs(args: IcsArgs): string {
  const end = new Date(args.start.getTime() + args.durationMinutes * 60_000)
  const description = [args.description, args.meetingUrl]
    .filter((part): part is string => Boolean(part))
    .join('\n')

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Beyond CTCI//Audition Call//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${args.uid}`,
    `DTSTAMP:${toIcsUtc(args.stamp)}`,
    `DTSTART:${toIcsUtc(args.start)}`,
    `DTEND:${toIcsUtc(end)}`,
    `SUMMARY:${escapeText(args.summary)}`,
    // Conditional: the meeting url arrives asynchronously from Google, so a
    // download taken seconds after confirming has no location to offer.
    ...(args.meetingUrl ? [`LOCATION:${args.meetingUrl}`] : []),
    ...(description ? [`DESCRIPTION:${escapeText(description)}`] : []),
    'END:VEVENT',
    'END:VCALENDAR',
  ]

  return `${lines.join('\r\n')}\r\n`
}
